import type { DocumentEmailHistoryType } from '@econtractor/types';
import { useEffect, useRef, useState } from 'react';
import type { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import * as apiDocument from 'api/apiDocument';
import * as apiProposal from 'api/apiProposal';
import { buildDocumentMailContent } from 'utils/helpers/email/buildMailContent';
import { EMAIL_BODY_MAX_LENGTH, type OffSendDocumentEmailProps, type SendDocumentEmailValues } from './types';

const parseEmails = (value: string): string[] => value.split(',').map((email) => email.trim()).filter(Boolean);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const useSendDocumentEmail = ({ objectId, target, onHide, onSent }: OffSendDocumentEmailProps) => {
    const { t } = useTranslation(['Proposal', 'common']);
    const { showToast } = useToast();
    const submitting = useRef(false);
    const [isSending, setIsSending] = useState(false);
    const [history, setHistory] = useState<DocumentEmailHistoryType[] | null>(null);
    const [historyError, setHistoryError] = useState(false);
    const generationId = target?.ID;
    const source = target?.source === 'lead' ? 'lead' : 'object';

    useEffect(() => {
        let active = true;
        setHistory(null);
        setHistoryError(false);
        if (generationId && objectId) {
            void apiDocument.getObjectDocumentEmailHistory(objectId, generationId, source).then((rows) => {
                if (!active) return;
                setHistory(rows);
                setHistoryError(rows === null);
            });
        }
        return () => { active = false; };
    }, [objectId, generationId, source]);

    const handleClose = () => {
        if (!submitting.current) onHide();
    };

    const validate = (values: SendDocumentEmailValues): FormikErrors<SendDocumentEmailValues> => {
        const emails = parseEmails(values.email);
        if (!emails.length) return { email: t('edit.generate.sendDialog.emailRequired') };
        const invalidEmails = emails.filter((email) => !EMAIL_RE.test(email));
        return invalidEmails.length
            ? { email: t('edit.generate.sendDialog.emailInvalid', { emails: invalidEmails.join(', ') }) }
            : {};
    };

    const handleSubmit = async (values: SendDocumentEmailValues) => {
        if (submitting.current || !target || Number(target.deleted || 0)) return;
        submitting.current = true;
        setIsSending(true);
        try {
            const isLeadProposal = (target.source || 'object') === 'lead';
            const fileUrl = String(target.filePublicUrl || '').trim();
            const leadId = Number(target.leadId || 0);
            if (!fileUrl || (isLeadProposal ? !leadId : !objectId)) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            const email = parseEmails(values.email).join(', ');
            const emailBody = values.emailBody.trim().slice(0, EMAIL_BODY_MAX_LENGTH);
            const mailContent = await buildDocumentMailContent({
                type: isLeadProposal ? 'proposal' : target.proposalType || 'other',
                documentName: target.proposalName || target.fileName || 'document',
                fileUrl,
                emailBody,
            });
            if (!mailContent) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            const payload = { email, emailBody, mailContent };
            const sent = isLeadProposal
                ? await apiProposal.sendLeadProposalGenerationEmail(leadId, target.ID, payload)
                : await apiDocument.sendObjectDocumentGenerationEmail(objectId, target.ID, payload);
            if (!sent) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            showToast(t('edit.generate.sendDialog.success'), 'success');
            onHide();
            await onSent();
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            submitting.current = false;
            setIsSending(false);
        }
    };

    return { handleClose, handleSubmit, validate, isSending, history, historyError };
};

export default useSendDocumentEmail;
